import { Type, type Schema } from '@google/genai';
import {
  deriveScreeningRecommendationSetV1,
  type ScreeningRecommendationReport,
} from '../lib/findings/screeningArchitectureRecommendations';

/**
 * Hand-rolled validator for the boundary between Gemini and the store.
 * No Zod dep — the schemas already live as Gemini Schema objects in
 * src/services/reportPrompts.ts; duplicating them in Zod invites drift.
 *
 * Walks `value` against `schema`, checking required object props, primitive
 * types and declared array bounds. Returns human-readable errors (empty = valid).
 *
 * Permissive on purpose:
 *  - Ignores extra properties (Gemini may emit unrequested fields).
 *  - Treats `Schema.nullable` as accepting null/undefined for that node.
 *  - On unknown schema types, accepts (so future Schema additions don't break).
 */
export interface ValidationResult {
  ok: boolean;
  errors: string[];
}

export function validateAgainstSchema(value: unknown, schema: Schema, path = '$'): ValidationResult {
  const errors: string[] = [];
  walk(value, schema, path, errors);
  return { ok: errors.length === 0, errors };
}

function walk(value: unknown, schema: Schema, path: string, errors: string[]): void {
  if (value === null || value === undefined) {
    if (schema.nullable) return;
    errors.push(`${path}: missing (expected ${schema.type})`);
    return;
  }

  switch (schema.type) {
    case Type.OBJECT: {
      if (typeof value !== 'object' || Array.isArray(value)) {
        errors.push(`${path}: expected object, got ${describe(value)}`);
        return;
      }
      const obj = value as Record<string, unknown>;
      const required = schema.required ?? [];
      for (const key of required) {
        if (!(key in obj) || obj[key] === undefined) {
          errors.push(`${path}.${key}: required field missing`);
        }
      }
      const props = schema.properties ?? {};
      for (const [key, subSchema] of Object.entries(props)) {
        if (key in obj && obj[key] !== undefined) {
          walk(obj[key], subSchema as Schema, `${path}.${key}`, errors);
        }
      }
      return;
    }
    case Type.ARRAY: {
      if (!Array.isArray(value)) {
        errors.push(`${path}: expected array, got ${describe(value)}`);
        return;
      }
      if (schema.minItems !== undefined && value.length < Number(schema.minItems)) {
        errors.push(`${path}: array shorter than minItems ${schema.minItems}`);
      }
      if (schema.maxItems !== undefined && value.length > Number(schema.maxItems)) {
        errors.push(`${path}: array longer than maxItems ${schema.maxItems}`);
      }
      const itemSchema = schema.items;
      if (itemSchema) {
        for (let i = 0; i < value.length; i++) {
          walk(value[i], itemSchema as Schema, `${path}[${i}]`, errors);
        }
      }
      return;
    }
    case Type.STRING: {
      if (typeof value !== 'string') {
        errors.push(`${path}: expected string, got ${describe(value)}`);
      } else if (schema.minLength !== undefined && value.length < Number(schema.minLength)) {
        errors.push(`${path}: string shorter than minLength ${schema.minLength}`);
      } else if (schema.pattern && !new RegExp(schema.pattern).test(value)) {
        errors.push(`${path}: string does not match required pattern`);
      } else if (Array.isArray(schema.enum) && schema.enum.length > 0 && !schema.enum.includes(value)) {
        errors.push(`${path}: value "${value}" not in enum [${schema.enum.join(', ')}]`);
      }
      return;
    }
    case Type.NUMBER:
    case Type.INTEGER: {
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        errors.push(`${path}: expected number, got ${describe(value)}`);
      }
      return;
    }
    case Type.BOOLEAN: {
      if (typeof value !== 'boolean') {
        errors.push(`${path}: expected boolean, got ${describe(value)}`);
      }
      return;
    }
    default:
      // Unknown / future Schema.type — accept.
      return;
  }
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

/**
 * Walk every string field in a Gemini-emitted response and collect the
 * citation numbers it cites via `[Ref N]` tokens. Used by
 * `validateCitationBounds` to reject responses that hallucinate ref
 * numbers beyond `references.length`.
 *
 * Visits arrays and nested objects recursively; ignores non-string
 * leaves. The token shape is intentionally narrow — `[Ref N]` with no
 * extra whitespace inside the brackets — to match what
 * runReportQualityPipeline / citationValidation already enforce
 * downstream. Looser shapes like `[ref n]` or `[Ref N, M]` would not
 * be caught here, but they're not produced by the model either.
 */
export const CITATION_TOKEN_RE = /\[Ref (\d+)\]/g;

export function collectCitationNumbers(value: unknown): number[] {
  const out: number[] = [];
  visit(value);
  return out;

  function visit(v: unknown): void {
    if (v === null || v === undefined) return;
    if (typeof v === 'string') {
      let m: RegExpExecArray | null;
      // Regex is global — reset lastIndex so the same instance can be
      // reused across siblings without dropping later matches.
      CITATION_TOKEN_RE.lastIndex = 0;
      while ((m = CITATION_TOKEN_RE.exec(v)) !== null) {
        const n = Number(m[1]);
        if (Number.isFinite(n)) out.push(n);
      }
      return;
    }
    if (Array.isArray(v)) {
      for (const item of v) visit(item);
      return;
    }
    if (typeof v === 'object') {
      for (const item of Object.values(v as Record<string, unknown>)) visit(item);
    }
  }
}

/**
 * Reject a Gemini response whose narrative cites [Ref N] tokens
 * beyond the appendix's actual upper bound. Soft-fail at the call
 * site — the SiteRagService catch turns this into degraded mode so a
 * usable (deterministic) report still ships.
 *
 * `referenceCount === 0` disables the check (no SOURCES list to bound
 * against) so generation paths that don't pre-build references still
 * pass through. Out-of-bounds detection above 0 is strict.
 */
export function validateCitationBounds(value: unknown, referenceCount: number): ValidationResult {
  const errors: string[] = [];
  if (!Number.isFinite(referenceCount) || referenceCount <= 0) {
    return { ok: true, errors };
  }
  const cited = collectCitationNumbers(value);
  const offenders = Array.from(new Set(cited.filter(n => n < 1 || n > referenceCount))).sort((a, b) => a - b);
  if (offenders.length > 0) {
    errors.push(
      `citations: ${offenders.length} out-of-bounds [Ref N] token${offenders.length === 1 ? '' : 's'} ` +
      `(${offenders.slice(0, 6).map(n => `[Ref ${n}]`).join(', ')}${offenders.length > 6 ? ', …' : ''}); ` +
      `references list has ${referenceCount} entries`,
    );
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Mode-sensitive validator for Feasibility `recommendations[]` (`recommend` vs
 * `evaluate-proposal`). Enforces non-blank `title`, `description`, and `rationale`,
 * requires coverage of `power`, `cooling`, and `site` categories, and in `recommend`
 * mode verifies that the consumer contract (`deriveScreeningRecommendationSetV1`)
 * resolves each architecture question to `recommended`, `alternatives`, or an
 * explicit `unresolved` reason rather than dropping into silent `not-produced`.
 */
export function validateFeasibilityRecommendationsForMode(
  value: unknown,
  mode: 'recommend' | 'evaluate-proposal' = 'recommend',
): ValidationResult {
  const errors: string[] = [];
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { ok: false, errors: ['$: expected feasibility response object'] };
  }
  const obj = value as Record<string, unknown>;
  const recs = obj.recommendations;
  if (!Array.isArray(recs)) {
    return { ok: false, errors: ['$.recommendations: expected array'] };
  }
  if (recs.length < 3) {
    errors.push(`$.recommendations: expected at least 3 recommendations (power, cooling, site), got ${recs.length}`);
  }
  const seenTypes = new Set<string>();
  for (let i = 0; i < recs.length; i++) {
    const item = recs[i];
    const path = `$.recommendations[${i}]`;
    if (!item || typeof item !== 'object' || Array.isArray(item)) {
      errors.push(`${path}: expected object`);
      continue;
    }
    const r = item as Record<string, unknown>;
    if (r.type !== 'power' && r.type !== 'cooling' && r.type !== 'site') {
      errors.push(`${path}.type: expected "power" | "cooling" | "site", got ${JSON.stringify(r.type)}`);
    } else {
      seenTypes.add(r.type);
    }
    for (const field of ['title', 'description', 'rationale'] as const) {
      if (typeof r[field] !== 'string' || !/\S/.test(r[field] as string)) {
        errors.push(`${path}.${field}: required non-blank string missing`);
      }
    }
    if (r.architecture !== undefined && (typeof r.architecture !== 'string' || !/\S/.test(r.architecture))) {
      errors.push(`${path}.architecture: when provided, must be a non-blank string`);
    }
  }
  for (const requiredType of ['power', 'cooling', 'site'] as const) {
    if (!seenTypes.has(requiredType)) {
      errors.push(`$.recommendations: missing required "${requiredType}" recommendation category`);
    }
  }
  if (mode === 'recommend' && errors.length === 0) {
    const syntheticReport: ScreeningRecommendationReport = {
      id: 'validation-preview',
      siteId: 'validation-site',
      reportType: 'feasibility',
      siteName: 'Validation Site',
      address: 'US',
      location: { lat: 0, lng: 0, address: 'US' },
      overallScore: 70,
      overallStatus: 'amber',
      summary: String(obj.executiveSummary ?? 'Summary'),
      timeSeries: [],
      risks: [],
      createdAt: '2026-10-01T00:00:00Z',
      projectIntentAtGen: { screeningMode: 'recommend' } as never,
      recommendations: recs as never,
      btmDetailedAnalysis: Array.isArray(obj.btmDetailedAnalysis) ? (obj.btmDetailedAnalysis as never) : [],
      developmentPathway: Array.isArray(obj.developmentPathway) ? (obj.developmentPathway as never) : [],
      maxCampusCapacity: typeof obj.maxCampusCapacity === 'string' ? obj.maxCampusCapacity : undefined,
      reportRevision: {
        schemaVersion: 'report-revision-ref-v1',
        revisionId: 'rev-1',
        sourcePath: 'users/u/sites/s/reports/source.json',
        sourceHash: 'a'.repeat(64),
        documentPath: 'users/u/sites/s/reports/document.json',
        documentHash: 'b'.repeat(64),
        presentationPath: 'users/u/sites/s/reports/presentation.json',
        presentationHash: 'c'.repeat(64),
        artifacts: {},
        deliveryDecision: {
          schemaVersion: 'delivery-decision-v1',
          state: 'ready',
          canViewPrivate: true,
          canExport: true,
          canPublish: true,
          reasons: [],
        },
      },
    };
    const set = deriveScreeningRecommendationSetV1(syntheticReport, 'validation-site');
    const questionRequirements: Array<{ id: keyof typeof set.questions; message: string }> = [
      {
        id: 'btm-architecture',
        message: '$.recommendations: recommend mode requires an extractable power supply architecture (btm-architecture) or explicit unresolved reason',
      },
      {
        id: 'power-type',
        message: '$.recommendations: recommend mode requires an extractable power technology/supply option (power-type) or explicit unresolved reason',
      },
      {
        id: 'cooling-strategy',
        message: '$.recommendations: recommend mode requires an extractable rack cooling loop strategy (cooling-strategy) or explicit unresolved reason',
      },
      {
        id: 'cooling-type',
        message: '$.recommendations: recommend mode requires an extractable heat rejection strategy (cooling-type) or explicit unresolved reason',
      },
      {
        id: 'dc-power',
        message: '$.recommendations: recommend mode requires a "Recommended screening size range: ..." (dc-power) or explicit unresolved sizing reason',
      },
    ];
    for (const req of questionRequirements) {
      if (set.questions[req.id].status === 'not-produced') {
        errors.push(req.message);
      }
    }
  }
  return { ok: errors.length === 0, errors };
}
